import { listingWithinAvailableHours } from './presence';
/**
 * Listener availability statuses for talkers.
 *
 * Preference (`publicData.availableNow`) is only flipped by the listener’s
 * Available to Listen toggle. A live session never writes that flag.
 *
 *  - available: preference ON, not in a live session
 *  - in_conversation: live session (stays in search; never shown Offline)
 *  - offline: manual OFF only
 */

export const LISTENER_STATUS = {
  AVAILABLE: 'available',
  IN_CONVERSATION: 'in_conversation',
  // Available on but no live heartbeat (Sam, Oct 26 2026): looks like another session.
  BUSY: 'busy',
  // Available on but not reachable live right now (Oct 29 2026): "Texts you", Book later.
  TEXTS_YOU: 'texts_you',
  OFFLINE: 'offline',
};

export const LISTENER_PRIMARY_CTA = {
  TALK_NOW: 'talk_now',
  BOOK_LATER: 'book_later',
  BOOK_NEXT: 'book_next',
  VIEW_AVAILABILITY: 'view_availability',
};

/**
 * Available to Listen preference. Defaults OFF (Sam, Oct 3 2026): only an
 * explicit `true` that the listener switched on is Available Now. A missing
 * flag is Offline (profile visible with View Availability).
 *
 * @param {Object} [listing]
 * @returns {boolean}
 */
export const listingAvailabilityPreferenceOn = listing =>
  listingWithinAvailableHours(listing);

/**
 * Epoch ms from a presence row or URL value.
 *
 * @param {unknown} value
 * @returns {number|null}
 */
export const parseNextAvailableAt = value => {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      return numeric;
    }
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
};

/**
 * Clock label for “Next available at 1:30 PM”.
 *
 * @param {number|null} [ms]
 * @param {string} [timeZone]
 * @returns {string|null}
 */
export const formatNextAvailableClock = (ms, timeZone) => {
  const at = parseNextAvailableAt(ms);
  if (!at) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    }).format(new Date(at));
  } catch (e) {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(at));
  }
};

/**
 * Derive the talker-facing status and CTAs from listing + presence extras.
 *
 * @param {Object} [params]
 * @param {Object} [params.listing]
 * @param {boolean} [params.online]
 * @param {boolean} [params.sessionBusy]
 * @param {boolean} [params.startNowPaused]
 * @param {number|string|null} [params.nextAvailableAt]
 * @returns {{
 *   status: string,
 *   talkNow: boolean,
 *   primaryCta: string,
 *   secondaryCta: string|null,
 *   nextAvailableAt: number|null,
 * }}
 */
export const resolveListenerAvailability = ({
  listing,
  online = false,
  sessionBusy = false,
  startNowPaused = false,
  nextAvailableAt = null,
  away = false,
} = {}) => {
  const nextAt = parseNextAvailableAt(nextAvailableAt);
  // Live session wins so a busy listener is never a dead end / Offline.
  if (sessionBusy) {
    return {
      status: LISTENER_STATUS.IN_CONVERSATION,
      talkNow: false,
      primaryCta: LISTENER_PRIMARY_CTA.BOOK_NEXT,
      secondaryCta: null,
      nextAvailableAt: nextAt,
    };
  }
  const availablePref = listingAvailabilityPreferenceOn(listing) || online === true || away === true;
  if (!availablePref) {
    return {
      status: LISTENER_STATUS.OFFLINE,
      talkNow: false,
      primaryCta: LISTENER_PRIMARY_CTA.VIEW_AVAILABILITY,
      secondaryCta: null,
      nextAvailableAt: null,
    };
  }
  // Oct 29 2026 (Sam lock): flag on without live presence is never Available Now.
  if (online !== true) {
    return {
      status: LISTENER_STATUS.TEXTS_YOU,
      talkNow: false,
      primaryCta: LISTENER_PRIMARY_CTA.BOOK_LATER,
      secondaryCta: null,
      nextAvailableAt: nextAt,
    };
  }
  if (startNowPaused) {
    return {
      status: LISTENER_STATUS.AVAILABLE,
      talkNow: false,
      primaryCta: LISTENER_PRIMARY_CTA.BOOK_NEXT,
      secondaryCta: LISTENER_PRIMARY_CTA.BOOK_LATER,
      nextAvailableAt: nextAt,
    };
  }
  return {
    status: LISTENER_STATUS.AVAILABLE,
    talkNow: true,
    primaryCta: LISTENER_PRIMARY_CTA.TALK_NOW,
    secondaryCta: LISTENER_PRIMARY_CTA.BOOK_LATER,
    nextAvailableAt: null,
  };
};

/**
 * Badge copy id for a status. In-conversation uses the one-line next-slot
 * message when a clock time is known.
 *
 * @param {string} status
 * @param {string|null} [clockLabel]
 * @returns {string}
 */
export const listenerStatusMessageId = (status, clockLabel) => {
  if (status === LISTENER_STATUS.IN_CONVERSATION) {
    return clockLabel
      ? 'AvailableNowBadge.inConversationNext'
      : 'AvailableNowBadge.inConversation';
  }
  if (status === LISTENER_STATUS.BUSY) {
    return 'AvailableNowBadge.busy';
  }
  if (status === LISTENER_STATUS.TEXTS_YOU) {
    return 'AvailableNowBadge.textsYou';
  }
  if (status === LISTENER_STATUS.OFFLINE) {
    return 'AvailableNowBadge.offline';
  }
  return 'AvailableNowBadge.label';
};

/**
 * Card / profile primary CTA message id.
 *
 * @param {string} primaryCta
 * @returns {string}
 */
export const listenerPrimaryCtaMessageId = primaryCta => {
  if (primaryCta === LISTENER_PRIMARY_CTA.TALK_NOW) {
    return 'ListingCard.talkNow';
  }
  if (primaryCta === LISTENER_PRIMARY_CTA.BOOK_NEXT) {
    return 'ListingCard.bookNext';
  }
  if (primaryCta === LISTENER_PRIMARY_CTA.VIEW_AVAILABILITY) {
    return 'ListingCard.viewAvailability';
  }
  return 'ListingCard.bookLater';
};
